import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {makeReactContext} from 'lib/makeReactContext';
import {getProcedureTypeForStudyType} from 'studies/TypedStudyUtils';
import SetStudyPhaseDropdown from 'studies/workspace/overview/SetStudyPhaseDropdown';
import {FONT_PROXIMA_12} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Breadcrumbs, {CrumbLink} from 'ui-library/Breadcrumbs';
import {ButtonSize} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import {NavItem, TabContainerHeaderDivider} from 'ui-library/TabContainer';

import {StudyOverviewTabContentsProps} from '../utils/TypedStudyTypes';
import AddExperimentButton from '../utils/ui/AddExperimentButton';
import AnalyzeProcedureRunButton from '../utils/ui/AnalyzeProcedureRunButton';
import LinkToPreviousUnitOperationsButton from '../utils/ui/LinkToPreviousUnitOperationsButton';
import StudyExecutionHeader from './StudyExecutionHeader';

export enum ProcedureRunTab {
  OVERVIEW = 'overview',
  DESIGN = 'design',
}

export interface ProcedureRunStateSummary {
  id: string | null;
  name: string | null;
}

export const StudyOverviewContext = makeReactContext<{
  selectedProcedureRun: ProcedureRunStateSummary;
  setSelectedProcedureRun: (procedureRunStateSummary: ProcedureRunStateSummary) => void;
  currentProcedureRunTab: ProcedureRunTab;
}>(
  () => ({
    selectedProcedureRun: {id: null, name: null},
    setSelectedProcedureRun: () => {},
    currentProcedureRunTab: ProcedureRunTab.OVERVIEW,
  }),
  'StudyOverviewContext'
);

/*
 * Exported for testing
 */
export function studyOverviewCrumbs(props: {
  setSelectedProcedureRun: (procedureRunStateSummary: ProcedureRunStateSummary) => void;
  selectedProcedureRun: ProcedureRunStateSummary;
  studyName: string;
  setCurrentProcedureRunTab: (procedureRunTab: ProcedureRunTab) => void;
}): JSX.Element {
  return (
    <>
      <div {...getTestProps({component: 'MultiProcedureRunOverviewTabContents', element: 'breadcrumbs'})}>
        <Breadcrumbs>
          <CrumbLink
            onClick={() => {
              props.setSelectedProcedureRun({id: null, name: null});
              // resets the procedure run tab to overview when selecting a new procedure run
              props.setCurrentProcedureRunTab(ProcedureRunTab.OVERVIEW);
            }}
          >
            {props.studyName}
          </CrumbLink>
        </Breadcrumbs>
      </div>
      {props.selectedProcedureRun.name && (
        <div className={css(styles.breadcrumbsFont)}>{props.selectedProcedureRun.name}</div>
      )}
    </>
  );
}

/*
 * Exported for testing
 */
export function procedureRunViewerTabs(props: {
  selectedProcedureRunId: string | null;
  setCurrentProcedureRunTab: (procedureRunTab: ProcedureRunTab) => void;
  currentProcedureRunTab: ProcedureRunTab | null;
}): JSX.Element | null {
  return props.selectedProcedureRunId != null ? (
    <div className={css(styles.tabContainer)}>
      <NavItem
        key={ProcedureRunTab.OVERVIEW}
        active={props.currentProcedureRunTab === ProcedureRunTab.OVERVIEW}
        tabKey={ProcedureRunTab.OVERVIEW}
        title="OVERVIEW"
        onClick={props.setCurrentProcedureRunTab}
      />
      <NavItem
        key={ProcedureRunTab.DESIGN}
        active={props.currentProcedureRunTab === ProcedureRunTab.DESIGN}
        tabKey={ProcedureRunTab.DESIGN}
        title="DESIGN"
        onClick={props.setCurrentProcedureRunTab}
      />
      <TabContainerHeaderDivider />
    </div>
  ) : null;
}

export default function MultiProcedureRunOverviewTabContents(
  props: StudyOverviewTabContentsProps
): JSX.Element {
  const [selectedProcedureRun, setSelectedProcedureRun] = useState<ProcedureRunStateSummary>({
    id: null,
    name: null,
  });

  // When the user navigates to a procedure run from the study overview, we want to show the overview tab
  const [currentProcedureRunTab, setCurrentProcedureRunTab] = useState<ProcedureRunTab>(
    ProcedureRunTab.OVERVIEW
  );

  const studyOverviewContextValue = useMemo(
    () => ({
      selectedProcedureRun,
      setSelectedProcedureRun,
      currentProcedureRunTab,
    }),
    [currentProcedureRunTab, selectedProcedureRun]
  );

  return (
    <StudyOverviewContext.Provider value={studyOverviewContextValue}>
      <StudyExecutionHeader
        {...props}
        dataTestAttrs={{component: 'MultiProcedureRunOverviewTabContents', element: 'overview'}}
        executionContainerStyling={executionContainer}
        headerSectionContainerStyling={headerSectionContainerStyling}
        title={selectedProcedureRun.name ?? undefined}
        headerBreadcrumb={studyOverviewCrumbs({
          setSelectedProcedureRun,
          selectedProcedureRun,
          studyName: props.study.name,
          setCurrentProcedureRunTab,
        })}
        headerTabs={
          procedureRunViewerTabs({
            selectedProcedureRunId: selectedProcedureRun?.id ?? null,
            setCurrentProcedureRunTab,
            currentProcedureRunTab,
          }) ?? undefined
        }
        hideDescription={true}
        headerButtons={{
          buttons:
            selectedProcedureRun.id != null ? (
              <>
                {selectedProcedureRun.id &&
                  selectedProcedureRun.name &&
                  BenchlingConfig.STUDIES_ENABLE_UNIT_OPERATION_INSTANCE_LINKING && (
                    <LinkToPreviousUnitOperationsButton
                      studyId={props.study.id}
                      procedureRunId={selectedProcedureRun.id}
                      procedureRunName={selectedProcedureRun.name}
                    />
                  )}
                {selectedProcedureRun.id && selectedProcedureRun.name && (
                  <AnalyzeProcedureRunButton
                    studyId={props.study.id}
                    studyName={props.study.name}
                    studyHumanId={props.study.humanId}
                    procedureRunId={selectedProcedureRun.id}
                    procedureRunName={selectedProcedureRun.name}
                    procedureType={getProcedureTypeForStudyType(props.study.studySchema.studyType)}
                    actionOrigin={ActionOrigin.STUDY_OVERVIEW_PAGE}
                  />
                )}
              </>
            ) : (
              <>
                <SetStudyPhaseDropdown
                  study={{
                    id: props.study.id,
                    phase: props.study.phase,
                    studyType: props.study.studySchema.studyType,
                  }}
                  refetch={props.refetch}
                  isDisabled={!props.isEditable}
                  requiresApproval={props.isDesignApprovalRequiredToProceed}
                  buttonSize={ButtonSize.MEDIUM}
                  allowReturnToDesignPhase={props.allowReturnToDesignPhase}
                />
                <AddExperimentButton studyId={props.study.id} studyType={props.study.studySchema.studyType} />
              </>
            ),
          buttonsStyling: buttonStyling,
        }}
      />
    </StudyOverviewContext.Provider>
  );
}

const styles = StyleSheet.create({
  breadcrumbsFont: {
    fontSize: 13,
    color: Colors.GRAY_DARKER,
  },
  tabContainer: {
    display: 'flex',
    flexDirection: 'row',
    ...FONT_PROXIMA_12,
    color: Colors.GRAY_DARKER,
    marginBottom: -10,
    marginTop: 15,
  },
});

const executionContainer: React.CSSProperties = {
  flex: '1 0',
};

const buttonStyling: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 18,
  marginTop: -35,
};

const headerSectionContainerStyling: React.CSSProperties = {
  margin: '0 16px 16px',
};
